/**
 * Match a loyalty package upgrade across an availability refresh after bedbank rejects its old price.
 *
 * Availability regenerates each `rateId`, while `service.rateId` remains stable. Resolve the target
 * relation to its service rate id before comparing the old and refreshed rates.
 */

type PropertyRatePricing = App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing']

export interface RefreshedPropertyRateUpgrade {
  sourcePricing: PropertyRatePricing
  targetPricing: PropertyRatePricing
}

function toPricing(rate: App.RawPropertyRate): PropertyRatePricing {
  return { totals: rate.totals, luxPlusTotals: rate.luxPlusTotals }
}

/**
 * Return refreshed source and target pricing when the same loyalty package upgrade still exists.
 * Return `undefined` if either rate is gone or the relation changed.
 *
 * Only `package` targets are supported. A future loyalty room-upgrade target must be handled
 * explicitly.
 */
export default function findRefreshedPropertyRateUpgrade(
  upgrade: Pick<App.Checkout.PropertyRateLoyaltyUpgrade, 'source' | 'target'>,
  rooms: Array<App.RawPropertyRoomRates> | undefined,
): RefreshedPropertyRateUpgrade | undefined {
  if (!rooms?.length) return

  const rates = rooms.flatMap((room) => ('rates' in room ? Object.values(room.rates) : []))
  const sourceRate = rates.find((rate) => rate.service?.rateId === upgrade.source.serviceRateId)
  if (!sourceRate) return

  const targetRateId = sourceRate.relations?.upgradeTargets?.package
  if (!targetRateId) return

  const targetRate = rates.find((rate) => rate.rateId === targetRateId)
  if (targetRate?.service?.rateId !== upgrade.target.serviceRateId) return

  return {
    sourcePricing: toPricing(sourceRate),
    targetPricing: toPricing(targetRate),
  }
}
